"use client";

import { SpatiusAvatarContext } from "@/components/spatius-avatar/spatius-avatar-context";
import { useSpatiusAvatar } from "@/hooks/useSpatiusAvatar";
import type {
  UseSpatiusAvatarOptions,
  UseSpatiusAvatarSessionOptions,
} from "@/types/spatius-avatar";
import { SessionProvider } from "@livekit/components-react";
import { type ReactNode, useCallback, useMemo } from "react";

export interface SpatiusAvatarProviderProps
  extends UseSpatiusAvatarOptions, UseSpatiusAvatarSessionOptions {
  children: ReactNode;
}

export function SpatiusAvatarProvider({
  children,
  onDisconnect,
  ...options
}: SpatiusAvatarProviderProps) {
  const avatar = useSpatiusAvatar(options);
  const end = useCallback(async () => {
    try {
      await avatar.disconnect();
    } finally {
      onDisconnect?.();
    }
  }, [avatar, onDisconnect]);

  const session = useMemo(
    () => ({
      ...avatar.session,
      end,
    }),
    [avatar.session, end]
  );

  const value = useMemo(
    () => ({
      ...avatar,
      session,
    }),
    [avatar, session]
  );

  return (
    <SpatiusAvatarContext.Provider value={value}>
      <SessionProvider session={session}>{children}</SessionProvider>
    </SpatiusAvatarContext.Provider>
  );
}
